import React, { ReactElement, useEffect, createContext, useState } from 'react'
import CustomCursor from '@components/core/customCursor'

export type CustomCursorProviderProps = DBN.IReactDefaultProps

export interface ICustomCursorContext {
  setCursorType: (cursor: string) => void
  disableCursor: () => void
  enableCursor: () => void
}

export interface ICustomCursorPosition {
  x: number
  y: number
}

export const CustomCursorContext = createContext<ICustomCursorContext>({
  setCursorType: () => {
    return null
  },
  disableCursor: () => {
    return null
  },
  enableCursor: () => {
    return null
  },
})

export default function CustomCursorContextProvider({
  children,
}: CustomCursorProviderProps): ReactElement {
  const [type, setType] = useState<string>('')
  const [active, setActive] = useState<boolean>(false)
  const [hover, setHover] = useState<boolean>(false)

  const setCursorType = (cursorType: string) => {
    setType(cursorType)
  }

  const disableCursor = () => {
    setActive(false)
  }

  const enableCursor = () => {
    setActive(true)
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover)')

    const mediaHandler = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        setHover(true)
      } else {
        setHover(false)
      }
    }

    if (document.hasFocus()) {
      setActive(true)
    }

    if (mediaQuery.matches) {
      setHover(true)
    }

    const mouseMoveHandler = (event: MouseEvent) => {
      const { clientX, clientY, movementX, movementY } = event
      if (movementX !== 0 || movementY !== 0) {
        document.documentElement.style.setProperty(
          '--x',
          String(clientX) + 'px'
        )
        document.documentElement.style.setProperty(
          '--y',
          String(clientY) + 'px'
        )
      }
    }

    const touchHandler = (event: TouchEvent) => {
      const { clientX, clientY } = event.changedTouches[0]
      document.documentElement.style.setProperty('--x', String(clientX) + 'px')
      document.documentElement.style.setProperty('--y', String(clientY) + 'px')
    }

    const mouseEnterHandler = () => {
      setActive(true)
    }

    const mouseLeaveHandler = () => {
      setActive(false)
    }

    if (!document.documentElement.style.getPropertyValue('--x'))
      document.documentElement.style.setProperty('--x', '-100px')
    if (!document.documentElement.style.getPropertyValue('--y'))
      document.documentElement.style.setProperty('--y', '-100px')

    document.body.addEventListener('mousemove', mouseMoveHandler)
    document.body.addEventListener('touchstart', touchHandler)
    document.body.addEventListener('touchmove', touchHandler)
    document.body.addEventListener('touchend', touchHandler)
    document.body.addEventListener('mouseenter', mouseEnterHandler)
    document.body.addEventListener('mouseleave', mouseLeaveHandler)
    mediaQuery.addEventListener('change', mediaHandler)
    return () => {
      document.body.removeEventListener('mousemove', mouseMoveHandler)
      document.body.removeEventListener('touchstart', touchHandler)
      document.body.removeEventListener('touchmove', touchHandler)
      document.body.removeEventListener('touchend', touchHandler)
      document.body.removeEventListener('mouseenter', mouseEnterHandler)
      document.body.removeEventListener('mouseleave', mouseLeaveHandler)
      mediaQuery.removeEventListener('change', mediaHandler)
    }
  }, [setActive, setHover])

  return (
    <CustomCursorContext.Provider
      value={{ setCursorType, disableCursor, enableCursor }}
    >
      {children}
      {hover && active && <CustomCursor type={type} />}
    </CustomCursorContext.Provider>
  )
}
